import { SectionOrFieldGroupDto } from "../Schemas/SectionOrFieldGroupDto";
import { createPathToProp, isCheckboxFieldGroup, shouldHideFieldOrSection } from "../Utilities/mappers";
import { RequestParameterTypeEnum } from "../Enums/RequestParameterTypeEnum";
import { RequestParameterSectionDto } from "../Schemas/RequestParameterSectionDto";
import { RequestFieldDto } from "../Schemas/RequestFieldDto";
import { Section } from "./Section";
import { Field } from "./Field";
import { SectionOrFieldDto } from "../Schemas/SectionOrFieldDto";
import { RadioButtonsField } from "./RadioButtonsField";
import { RequestFormContext } from "../RequestFormContext";

export type ComponentSwitcherProps = RequestFormContext & {
    sectionOrFieldGroup: SectionOrFieldGroupDto;
    sectionContainIdentificatorFieldWithOtherField: boolean;
}

/// Because we work with recursive structures, this components will handle
/// if it should display a field or nested section.
export const ComponentSwitcher = (props: ComponentSwitcherProps) => {
    const { groupFieldType } = props.sectionOrFieldGroup;

    const onlyTextInputs = groupFieldType === RequestParameterTypeEnum.SHORT_TEXT;
    const isCheckboxGroup = isCheckboxFieldGroup(props.sectionOrFieldGroup);

    const isRadioButtonType = groupFieldType === RequestParameterTypeEnum.RADIO_BUTTON;

    if (onlyTextInputs || isCheckboxGroup) return ShortTextFields(props);

    if (isRadioButtonType) return <RadioButtonsField {...props} />

    return ResolveSectionOrField(props);
}

type ResolveSectionOrFieldProps = ComponentSwitcherProps;

const ResolveSectionOrField = (props: ResolveSectionOrFieldProps) => {
    const { sectionOrFieldGroup, ...restProps } = props;

    if (!sectionOrFieldGroup.children.length) return <>Sekcia je prázdna</> // This should never happen

    const firstChild = sectionOrFieldGroup.children[0];

    if (firstChild.section) return <ResolvedSection {...restProps} section={firstChild.section} />

    if (firstChild.field) return <ResolvedField {...restProps} field={firstChild.field} />

    return <>Žiadna sekcia alebo pole nebolo nájdené</>
}

type OmittedFields = "sectionOrFieldGroup";

type ResolvedSectionProps = Omit<ResolveSectionOrFieldProps, OmittedFields> & {
    section: RequestParameterSectionDto;
}

const ResolvedSection = (props: ResolvedSectionProps) => {
    return (
        <Section
            {...props}
            pathToProp={`${props.pathToProp}.${createPathToProp("children", 0)}.section`}
        />
    );
}

type ResolvedFieldProps = Omit<ResolveSectionOrFieldProps, OmittedFields> & {
    field: RequestFieldDto;
}

const ResolvedField = (props: ResolvedFieldProps) => {
    return (
        <Field
            {...props}
            pathToProp={`${props.pathToProp}.${createPathToProp("children", 0)}.field`}
        />
    );
}

const ShortTextFields = (props: ComponentSwitcherProps) => {
    const getFields = (): (JSX.Element | null)[] => {
        return props.sectionOrFieldGroup.children.map((child: SectionOrFieldDto, index: number) => {
            if (!child.field) return <>Definícia k inputom sa nenašla</>

            if (
                shouldHideFieldOrSection(
                    child.field,
                    props.request.current,
                    props.settings.current.odvetvieId,
                    props.getRequestFieldsRelationTask.value?.fields ?? []
                )
            ) {
                return null;
            }

            return (
                <Field
                    key={index}
                    {...props}
                    field={child.field}
                    pathToProp={`${props.pathToProp}.${createPathToProp("children", index)}.field`}
                />
            );
        });
    };

    return <>{getFields()}</>;
};